import { IconChevronRight, IconPercentage } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useState } from 'react';
import classNames from 'classnames';
import {
    NSRadioOption, NSButton, NSCheckbox, NSDropdown, NSInput,
} from 'bricks';
import { ISubproject } from 'views/ProFormaTable/types';
import { IOptionAnyValue } from 'bricks/types';
import { POPULATE_USES_METHOD } from 'views/ProFormaTable/constants';
import { useProFormaSocketContext } from '../../../socketContext/ProFormaSocketProvider';

interface ProjectItemProps {
    project: ISubproject;
    isLast: boolean;
    selectedProjectId?: number;
    onProjectSelect: (projectId: number) => void;
    selectedScenarios: Record<number, IOptionAnyValue | undefined>;
    handleScenarioSelect: (projectId: number) => (option: IOptionAnyValue | undefined) => void;
    budgetEscalation: string;
    handleBudgetEscalationChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    setUsesMethod: (method: string | null) => void;
}

const ProjectItem: React.FC<ProjectItemProps> = ({
    project,
    isLast,
    selectedProjectId,
    onProjectSelect,
    selectedScenarios,
    handleScenarioSelect,
    budgetEscalation,
    handleBudgetEscalationChange,
    setUsesMethod,
}) => {
    const {
        handleImportBudgetClassificationFromProject, isImportFromProjectBudgetClassificationLoading,
    } = useProFormaSocketContext();
    const [isBudgetEstimateChecked, setIsBudgetEstimateChecked] = useState(true);
    const [isCashFlowChecked, setIsCashFlowChecked] = useState(true);

    const budgetEstId = `budget-est-${project.id}`;
    const cashFlowId = `cash-flow-${project.id}`;
    const selectedScenario = selectedScenarios[project.id];
    const selectedScenarioId = project.budgetScenarios.filter(scenario => scenario.correlationId === selectedScenario?.value)[0]?.id;
    const isSelected = project.id === selectedProjectId;

    const importSuccessCallback = () => {
        setUsesMethod(POPULATE_USES_METHOD.START_FROM_SCRATCH.title);
    };

    const handleImportBudgetClassification = () => {
        const data = {
            scenarioId: selectedScenarioId,
            subprojectId: project.id,
            shouldImportBudgetLines: isBudgetEstimateChecked,
            shouldImportCurveFits: isCashFlowChecked,
            budgetMultiplierPercentage: Number(budgetEscalation),
        };
        handleImportBudgetClassificationFromProject(data, importSuccessCallback);
    };

    return (
        <div>
            <div
                className={classNames('ImportSourceListItem cursor--pointer p-2', {
                    'ImportSourceListItem--selected': isSelected,
                })}
                onClick={() => onProjectSelect(project.id)}
                role="presentation"
            >
                <div className="d-flex align-items-start">
                    <div className="mr-2">
                        <NSRadioOption
                            id={`radio-${project.id}`}
                            name="projectSelection"
                            checked={isSelected}
                            onChange={() => onProjectSelect(project.id)}
                            label=""
                        />
                    </div>
                    <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <div className="font-12 text-muted">{project.developmentProject.projectName}</div>
                                <div className="text-white font-weight-bold">{project.subprojectName}</div>
                            </div>
                            {isSelected && (
                                <NSButton
                                    text="Import"
                                    color="primary"
                                    icon={IconChevronRight}
                                    iconPosition="right"
                                    className="px-3"
                                    disabled={project.earlyPlanning && !selectedScenario}
                                    callback={handleImportBudgetClassification}
                                    isLoading={isImportFromProjectBudgetClassificationLoading}
                                />
                            )}
                        </div>
                        {isSelected && (
                            <div className="mt-2">
                                <div className="separator mb-2" />
                                <div className="d-flex mt-2 mb-1">
                                    <div className="d-flex flex-column gap-2">
                                        <div className="text-white">
                                            Budget categories will be imported. Select
                                            <br />
                                            additional items to add from this project:
                                        </div>
                                        <div className="d-flex flex-column mt-2">
                                            <NSCheckbox
                                                id={budgetEstId}
                                                labelText="Budget estimates ($/SF)"
                                                checked={isBudgetEstimateChecked}
                                                callback={() => setIsBudgetEstimateChecked(!isBudgetEstimateChecked)}
                                                size="sm"
                                                iconClassName="mr-2"
                                                labelClassName="text-white font-weight-normal font-12 d-flex align-items-center"
                                            />
                                            <NSCheckbox
                                                id={cashFlowId}
                                                labelText="Cash flow curve fits"
                                                checked={isCashFlowChecked}
                                                callback={() => setIsCashFlowChecked(!isCashFlowChecked)}
                                                size="sm"
                                                iconClassName="mr-2"
                                                labelClassName="text-white font-weight-normal font-12 d-flex align-items-center"
                                            />
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-between ml-4">
                                        {project.earlyPlanning && (
                                            <div className="mr-3" style={{ width: '320px' }}>
                                                <div className="text-white mb-1">
                                                    Scenario
                                                    <span className="text-danger pl-1">*</span>
                                                </div>
                                                <div>
                                                    <NSDropdown
                                                        options={project.budgetScenarios?.map(scenario => ({
                                                            value: scenario.correlationId,
                                                            label: scenario.name,
                                                        })) || []}
                                                        placeholder="Select scenario"
                                                        handleSelectOption={handleScenarioSelect(project.id)}
                                                        selectedOption={selectedScenario}
                                                        isFullWidth
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {(isBudgetEstimateChecked) && (
                                            <div>
                                                <div className="text-white mb-1">Budget escalation factor</div>
                                                <div className="">
                                                    <NSInput
                                                        type="number"
                                                        name="budgetEscalation"
                                                        placeholder="Enter amount"
                                                        value={budgetEscalation}
                                                        onChange={handleBudgetEscalationChange}
                                                        appendInputAddon={<NSIcon icon={IconPercentage} />}
                                                        max={100}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {!isSelected && !isLast && <div className="border-bottom my-0 mx-3 opacity-10" />}
        </div>
    );
};

export default ProjectItem;
